import { OrgContext } from "contexts/OrgContext";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import FlatCard from "components/UI-lib/FlatCard";
import ModelSelector from "./ModelSelector";
import "./style.scss";
import InputField from "components/UI-lib/InputField";
import {
  classNames,
  memoryToGiB,
  timeout,
  validateName,
} from "components/utils";
import {
  ArrowRightCircleIcon,
  ArrowRightIcon,
  CubeIcon,
  CurrencyDollarIcon,
  Square3Stack3DIcon,
} from "@heroicons/react/24/outline";
import Button from "components/UI-lib/Button";
import { BillingContext } from "contexts/BillingContext";
import { DeploymentsContext } from "contexts/DeploymentsContext";
import { AddCreditsModal } from "components/ConnectCloudsOrCardModal/AddCreditsModal";
import ConnectCloudsOrCardModal from "components/ConnectCloudsOrCardModal";
import { allPublicCloudsName, handleIsPaymentSetup } from "./utils";
import InlineNotification from "contexts/Notifications/InlineNotifications";
import { useHistory } from "react-router";
import agent, {
  DeploymentInstanceType,
  DeploymentModelProvider,
  getProviderString,
  Model,
  ModelHardwareRequirement,
} from "server";
import hfLogo from "assets/img/svg/hf-logo.svg";
import BackendSelector from "./BackendSelector";
import posthog from "posthog-js";
import { UserContext } from "contexts/UserContext";
import { useQuery } from "@tanstack/react-query";
import { dummyModels } from ".";

export interface ModelSelected {
  modelName: string;
  modelProvider: DeploymentModelProvider;
  modelId: string;
  modelHardwareRequirements: ModelHardwareRequirement[];
}

export interface BackendSelected {
  instanceType: DeploymentInstanceType;
}

export interface DeploymentCreateProps {}

const ENABLE_STAGGER_ANIMATION = true;

const DeploymentCreate: React.FC = () => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const billingContext = useContext(BillingContext);
  const deploymentsContext = useContext(DeploymentsContext);
  const history = useHistory();

  //deploymentState
  const [model, setModel] = useState<ModelSelected>({
    modelName: "",
    modelProvider: DeploymentModelProvider.UNSPECIFIED,
    modelId: "",
    modelHardwareRequirements: [],
  });
  const [isReset, setIsReset] = useState(false);
  const [backend, setBackend] = useState<BackendSelected>();
  const [name, setName] = useState<string>("awesome-deployment-name");
  const [nameErrorMessage, setErrorMessage] = useState<string>("");

  const [selectedCloudName, setSelectedCloudName] =
    useState(allPublicCloudsName);
  const [loading, setLoading] = useState(false);

  const [minInstances, setMinInstances] = useState(1);
  const [maxInstances, setMaxInstances] = useState(1);
  const [maxInstancesError, setMaxInstancesError] = useState<string>("");
  const [minInstancesError, setMinInstancesError] = useState<string>("");

  // Credit card modal state
  const [showConnectCloudsOrCardModal, setShowConnectCloudsOrCardModal] =
    useState<boolean>(false);
  const [inlineAlertMessage, setInlineAlertMessage] = useState("");
  const [inlineAlertAction, setInlineAlertAction] = useState<any>(<></>);
  const [inlineAlertSeverity, setInlineAlertSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("error");

  const [noCardOnFile, setNoCardOnFile] = useState<boolean>(false);
  const [displayModels, setDisplayModels] = useState<Model[]>([]);

  useEffect(() => {
    handleIsPaymentSetup(selectedCloudName, orgContext.activeOrgId).then(
      (isPaymentSetUp) => setNoCardOnFile(!isPaymentSetUp)
    );
  }, [orgContext.workspaceGroups, selectedCloudName]);

  useEffect(() => {
    if (orgContext.activeOrgId != "") {
      deploymentsContext.getDeploymentInstanceTypes(orgContext.activeOrgId);
    }
  }, [orgContext.activeOrgId]);

  const staggerLoadModels = async (models: Model[]): Promise<void> => {
    setDisplayModels([]);
    let displayedModels: Model[] = [];

    if (!ENABLE_STAGGER_ANIMATION) {
      setDisplayModels(models);
      return;
    }

    for (let i = 0; i < models.length; i++) {
      await timeout(150);
      displayedModels = [...displayedModels, models[i]];
      setDisplayModels([...displayedModels]);
    }
  };

  const { data: allowedModelList = [], isLoading: isLoadingModels } = useQuery({
    queryKey: ["models", orgContext.activeOrgId],
    queryFn: async () => {
      const res = await agent.Models.getModels(orgContext.activeOrgId);
      if (!res.success || !res.data) {
        throw new Error(res.message || "Failed to fetch models");
      }

      return res.data;
    },
    enabled: !!orgContext.activeOrgId,
    onError: (error) => {
      console.error("Failed to fetch models:", error);
    },
  });

  useEffect(() => {
    if (allowedModelList.length === 0) {
      return;
    } else {
      staggerLoadModels(allowedModelList);
    }
  }, [allowedModelList]);

  // Effect to update max instances when backend changes
  useEffect(() => {
    if (backend) {
      const instanceTypeMaxInstances = backend.instanceType?.max_instances;

      // Ensure minInstances is at least 0
      if (minInstances < 0) {
        setMinInstances(0);
        setMinInstancesError("Min instances adjusted to 0");
      }

      // If current maxInstances is greater than the new backend's limit, adjust it
      if (instanceTypeMaxInstances && maxInstances > instanceTypeMaxInstances) {
        setMaxInstances(instanceTypeMaxInstances);
        setMaxInstancesError(
          `Adjusted to maximum allowed: ${instanceTypeMaxInstances}`
        );
      } else if (maxInstances < 1) {
        // Ensure maxInstances is at least 1
        setMaxInstances(1);
        setMaxInstancesError("Max instances must be at least 1");
      } else {
        setMaxInstancesError("");
      }
    }
  }, [backend]);

  const handleNameChange = (val: string) => {
    setName(val);
    // Set validation error messaging
    if (val === "") {
      setErrorMessage("Name required!");
      return;
    }
    const isValid = validateName(val);
    if (!isValid) {
      setErrorMessage("Name can only have letters, numbers and dashes.");
      return;
    }
    setErrorMessage("");
  };

  // need a field for model name
  const handleCreate = async () => {
    setInlineAlertMessage("");
    setLoading(true);
    const res = await agent.Deployments.createDeployments(
      orgContext.activeOrgId,
      {
        name: name,
        deployment_provider_cred_id:
          backend?.instanceType?.deployment_provider_cred_id,
        instance_type: backend?.instanceType?.type,
        location: backend?.instanceType?.location,
        min_instances: minInstances,
        max_instances: maxInstances,
        artifact: {
          model: {
            model_id: model.modelId,
          },
        },
      }
    );

    posthog.capture("Creating Deployment", {
      name: name,
      deploymentProviderCredID:
        backend?.instanceType?.deployment_provider_cred_id,
      instanceType: backend?.instanceType?.type,
      gpuName: backend?.instanceType.supported_gpus
        ? backend?.instanceType.supported_gpus[0].name
        : "Unknown",
      location: backend?.instanceType?.location,
      minInstances: minInstances,
      maxInstances: maxInstances,
      modelName: model.modelName,
      modelProvider: getProviderString(model.modelProvider),
      orgID: orgContext.activeOrgId,
      userID: userContext.me?.id,
      wasSuccess: res.success,
      errorMessage: !res.success ? res.message || "" : "",
    });

    if (res.success && res.data) {
      // TO DO: deployment returned should populate context
      console.log("succesful deployment", res);
      history.push(
        `/org/${orgContext.activeOrgId}/deployments/${res.data.deployment_id}`
      );
    } else {
      setInlineAlertSeverity("error");
      setInlineAlertMessage(res.message || "");
    }
    setLoading(false);
  };

  const filterInstanceTypesByHardwareRequirements = useMemo(() => {
    if (
      !model?.modelHardwareRequirements?.length ||
      !deploymentsContext.instanceTypes?.length
    ) {
      return deploymentsContext.instanceTypes;
    }

    return deploymentsContext.instanceTypes.filter((instanceType) => {
      // Check if the instance type meets all hardware requirements
      return model.modelHardwareRequirements.every((requirement) => {
        // If no supported GPUs specified in requirements, skip GPU checks
        if (!requirement.supported_gpus?.length) {
          return true;
        }

        // If instance has no GPUs but requirements need them, filter out
        if (!instanceType.supported_gpus?.length) {
          console.log("no instance gpus");
          return false;
        }

        const meetsVramRequirement = requirement.min_vram_gb
          ? instanceType.supported_gpus?.every(
              (gpu) =>
                memoryToGiB(gpu.memory || "0B") >=
                memoryToGiB(requirement.min_vram_gb)
            )
          : true;

        // If VRAM requirement isn't met, no need to check further
        if (!meetsVramRequirement) {
          return false;
        }

        // Then check GPU configuration requirements
        return instanceType.supported_gpus?.every((instanceGpu) => {
          return requirement.supported_gpus.some((requiredGpu) => {
            return (
              instanceGpu.name === requiredGpu.name &&
              instanceGpu.count === requiredGpu.count
            );
          });
        });
      });
    });
  }, [model.modelHardwareRequirements, deploymentsContext.instanceTypes]);

  const costPerInstanceHour = useMemo(() => {
    return (
      (backend?.instanceType?.supported_gpus?.[0].count || 1) *
      parseFloat(backend?.instanceType?.price_per_gpu_hour.amount || "0.00")
    );
  }, [backend]);

  return (
    <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 pb-10 pt-3">
      {billingContext.billingProfile?.billing_type === "credit" ? (
        <AddCreditsModal
          show={showConnectCloudsOrCardModal}
          setShow={setShowConnectCloudsOrCardModal}
          onCancel={() => {
            setShowConnectCloudsOrCardModal(false);
          }}
          setNoCardOnFile={setNoCardOnFile}
          noCardOnFile={noCardOnFile}
          onSuccess={() => {
            setShowConnectCloudsOrCardModal(false);
            setInlineAlertSeverity("success");
            setInlineAlertMessage("Credit card details saved successfully!");
          }}
        />
      ) : (
        <ConnectCloudsOrCardModal
          show={showConnectCloudsOrCardModal}
          setShow={setShowConnectCloudsOrCardModal}
          onCancel={() => {
            setShowConnectCloudsOrCardModal(false);
          }}
          setNoCardOnFile={setNoCardOnFile}
          onSuccess={() => {
            setShowConnectCloudsOrCardModal(false);
            setInlineAlertSeverity("success");
            setInlineAlertMessage("Credit card details saved successfully!");
          }}
          setSelectedCloudName={setSelectedCloudName}
          selectedCloudName={selectedCloudName}
        />
      )}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex mt-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
            Create a Deployment
          </h1>
        </div>
        <div className="flex flex-row mt-2">
          {model.modelName !== "" && (
            <div
              onClick={() => {
                setIsReset(true);
                setModel({
                  modelName: "",
                  modelProvider: DeploymentModelProvider.UNSPECIFIED,
                  modelId: "",
                  modelHardwareRequirements: [],
                });
              }}
              className="cursor-pointer"
            >
              <div className="flex flex-row group">
                <div className="flex flex-row text-highlight group-hover:text-highlight items-center mr-2">
                  <CubeIcon className={`w-5 h-5 mr-1`} />
                  <div>Model Selected: </div>
                </div>
                <div className="flex flex-row items-center text-secondary group-hover:text-highlight mt-[1px]">
                  {model.modelProvider ===
                    DeploymentModelProvider.HUGGING_FACE && (
                    <img
                      src={hfLogo}
                      alt="Hugging Face"
                      className="w-6 h-6 mr-1"
                    />
                  )}
                  <div className="text-sm">{model.modelName}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
        <div className="flex flex-col mt-5">
          <AnimatePresence initial={false}>
            {model.modelName === "" && (
              <motion.section
                key="container"
                initial={isReset ? "collapsed" : "open"}
                animate="open"
                exit="collapsed"
                variants={{
                  open: { opacity: 1, height: "auto" },
                  collapsed: { opacity: 0, height: 0 },
                }}
                transition={{
                  duration: isReset ? 0 : 0.8,
                  ease: [0.04, 0.62, 0.23, 0.98],
                }}
              >
                <motion.div
                  variants={{
                    collapsed: { scale: 0.8 },
                    open: { scale: 1 },
                  }}
                  transition={{ duration: isReset ? 0 : 0.8 }}
                  className="content-placeholder"
                >
                  <ModelSelector
                    isLoadingModels={isLoadingModels}
                    setReset={setIsReset}
                    setModel={setModel}
                    allowedModelList={displayModels}
                  />
                </motion.div>
              </motion.section>
            )}
            {model.modelName !== "" && (
              <>
                <BackendSelector
                  selectedBackend={backend}
                  setSelectedBackend={setBackend}
                  instanceTypes={filterInstanceTypesByHardwareRequirements}
                  allInstanceTypes={deploymentsContext.instanceTypes}
                />
                {!!backend && (
                  <div className="">
                    <hr className="w-[100%] my-5 dark:border dark:border-zinc-800" />
                    <div className="flex flex-row">
                      <div className="flex flex-col flex-1">
                        <div className="flex flex-col">
                          <div className="text-white flex flex-row items-center mb-2">
                            <Square3Stack3DIcon className="w-5 h-5 mr-1" />
                            <h2 className="col-span-full text-lg font-semibold text-white">
                              Specify Instances
                            </h2>
                          </div>
                          <div className="flex flex-row max-w-[500px]">
                            <InputField
                              className="flex-1 mr-3"
                              label="Min Instances"
                              type="number"
                              value={minInstances.toString()}
                              onChange={(val) => {
                                const min = parseInt(val);
                                if (min < 1) {
                                  setMinInstances(1);
                                  setMinInstancesError(
                                    "Min instances must be at least 1"
                                  );
                                } else if (min > maxInstances) {
                                  setMinInstances(maxInstances);
                                  setMinInstancesError(
                                    "Min instances cannot exceed max instances"
                                  );
                                } else {
                                  setMinInstances(min);
                                  setMinInstancesError("");
                                }
                              }}
                              placeholder=""
                              errorMessage={minInstancesError}
                            />
                            <InputField
                              className="flex-1"
                              label="Max Instances"
                              type="number"
                              value={maxInstances.toString()}
                              onChange={(val) => {
                                const max = parseInt(val);
                                // Get the instance type's maximum allowed instances
                                const instanceTypeMaxInstances =
                                  backend?.instanceType?.max_instances;

                                if (max < 1) {
                                  setMaxInstances(1);
                                  setMaxInstancesError(
                                    "Max instances must be at least 1"
                                  );
                                } else if (
                                  instanceTypeMaxInstances &&
                                  max > instanceTypeMaxInstances
                                ) {
                                  // Show error message when user tries to exceed the limit
                                  setMaxInstancesError(
                                    `Maximum allowed: ${instanceTypeMaxInstances}`
                                  );
                                  // Still set to max allowed value
                                  setMaxInstances(instanceTypeMaxInstances);
                                } else if (max < minInstances) {
                                  setMaxInstances(minInstances);
                                  setMaxInstancesError(
                                    "Max cannot be less than min instances"
                                  );
                                } else {
                                  setMaxInstances(max);
                                  setMaxInstancesError("");
                                }
                              }}
                              placeholder=""
                              errorMessage={maxInstancesError}
                            />
                          </div>
                        </div>
                        <div>
                          <h2 className="col-span-full text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
                            Name your Deployment
                          </h2>

                          <InputField
                            label=""
                            value={name}
                            onChange={handleNameChange}
                            errorMessage={nameErrorMessage}
                            className="w-[400px]"
                          />
                        </div>
                      </div>

                      <div className="flex flex-row flex-1">
                        <div className="flex flex-col flex-1">
                          <div className="text-white flex flex-row items-center mb-2">
                            <CurrencyDollarIcon className="w-5 h-5 mr-1" />
                            <h2 className="col-span-full text-lg font-semibold text-white">
                              Cost Calculation
                            </h2>
                          </div>
                          <div className="bg-secondaryBg p-3 rounded-lg space-y-1 text-secondary border border-zinc-800">
                            <div>
                              <div className="text-sm space-y-1 font-mono">
                                <p>
                                  Cost per instance: $
                                  {costPerInstanceHour.toFixed(2)}/hour
                                </p>
                                <p>
                                  Min cost: ${costPerInstanceHour.toFixed(2)} ×{" "}
                                  {isNaN(minInstances) ? "?" : minInstances}{" "}
                                  instance(s) = $
                                  {isNaN(minInstances)
                                    ? "?"
                                    : (
                                        costPerInstanceHour * minInstances
                                      ).toFixed(2)}
                                  /hour
                                </p>
                                <p>
                                  Max cost: ${costPerInstanceHour.toFixed(2)} ×{" "}
                                  {isNaN(maxInstances) ? "?" : maxInstances}{" "}
                                  instance(s) = $
                                  {isNaN(maxInstances)
                                    ? "?"
                                    : (
                                        costPerInstanceHour * maxInstances
                                      ).toFixed(2)}
                                  /hour
                                </p>
                              </div>
                            </div>

                            <div className="border-t border-zinc-800 pt-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm text-muted-foreground">
                                    Minimum Daily Cost
                                  </div>
                                  <div className="text-xl font-bold">
                                    $
                                    {(
                                      costPerInstanceHour *
                                      minInstances *
                                      24
                                    ).toFixed(2)}
                                  </div>
                                </div>
                                <ArrowRightIcon className="mx-2 w-5 h-5" />
                                <div>
                                  <div className="text-sm text-secondary">
                                    Maximum Daily Cost
                                  </div>
                                  <div className="text-xl font-bold">
                                    $
                                    {(
                                      costPerInstanceHour *
                                      maxInstances *
                                      24
                                    ).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                * The number of instances will fluctuate
                                depending on usage load
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col mt-5">
                      <div>
                        <Button
                          className={classNames(
                            "mt-2",
                            nameErrorMessage ? "" : "pulse-ready-button"
                          )}
                          type={"primary"}
                          label={false ? "Deploying" : "Deploy"}
                          disabled={
                            !!nameErrorMessage || !minInstances || !maxInstances
                          }
                          loading={loading}
                          onClick={handleCreate}
                          data-testid="deploy-button"
                        />
                      </div>
                      <InlineNotification
                        show={!!inlineAlertMessage}
                        severity={inlineAlertSeverity}
                        className="w-full mt-5 pb-2"
                        text={inlineAlertMessage}
                        text2={
                          <div className="mt-2 ml-[36px]">
                            {inlineAlertAction}
                          </div>
                        }
                        autoClose={false}
                        onClose={() => {
                          setInlineAlertMessage("");
                          setInlineAlertAction(<></>);
                          setInlineAlertSeverity("error");
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default DeploymentCreate;

export interface ModelPreviewProps {
  modelName: string;
  modelProvider: DeploymentModelProvider;
}

const ModelPreview: React.FC<ModelPreviewProps> = ({
  modelName: modelName,
  modelProvider,
}) => {
  return (
    <FlatCard className="animate-border cursor-pointer">
      <div className="flex flex-row items-center">
        <div className="flex flex-row">
          <div className="flex flex-row text-white items-center mr-2">
            <CubeIcon className={`w-5 h-5 mr-1`} />
            <div>Model Selected: </div>
          </div>
          <div className="flex flex-row items-center">
            {modelProvider === DeploymentModelProvider.HUGGING_FACE && (
              <img src={hfLogo} alt="Hugging Face" className="w-6 h-6 mr-1" />
            )}
            <div className="text-sm">{modelName}</div>
          </div>
        </div>
      </div>
      <span className="btnBefore"></span>
      <span className="btnAfter"></span>
    </FlatCard>
  );
};
